import { useState, useEffect } from 'react';

// Types for consent management
export interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  hasConsented: boolean;
  timestamp: number;
}

export interface ConsentBannerConfig {
  position: 'bottom' | 'top';
  theme: 'light' | 'dark';
  showRejectAll: boolean;
  privacyPolicyUrl?: string;
}

// Default consent state
const DEFAULT_CONSENT: ConsentState = {
  analytics: false,
  marketing: false,
  functional: true, // Essential cookies are always allowed
  hasConsented: false,
  timestamp: 0,
};

// Storage key for consent
const CONSENT_STORAGE_KEY = 'mcp-cloud-consent';
const CONSENT_EXPIRY_DAYS = 365;

// Utility functions for consent management
export class ConsentManager {
  private static instance: ConsentManager;
  private listeners: Array<(consent: ConsentState) => void> = [];
  private currentConsent: ConsentState = DEFAULT_CONSENT;

  static getInstance(): ConsentManager {
    if (!ConsentManager.instance) {
      ConsentManager.instance = new ConsentManager();
    }
    return ConsentManager.instance;
  }

  constructor() {
    this.loadConsent();
  }

  // Load consent from localStorage
  private loadConsent(): void {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (stored) {
        const consent = JSON.parse(stored) as ConsentState;
        
        // Check if consent is still valid (not expired)
        const expiryTime = consent.timestamp + (CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
        if (Date.now() < expiryTime) {
          this.currentConsent = consent;
        } else {
          // Consent expired, reset to default
          this.currentConsent = DEFAULT_CONSENT;
          this.saveConsent();
        }
      }
    } catch (error) {
      console.warn('Failed to load consent from storage:', error);
      this.currentConsent = DEFAULT_CONSENT;
    }
  }

  // Save consent to localStorage
  private saveConsent(): void {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(this.currentConsent));
    } catch (error) {
      console.warn('Failed to save consent to storage:', error);
    }
  }

  // Get current consent state
  getConsent(): ConsentState {
    return { ...this.currentConsent };
  }

  // Check if user has made a consent choice
  hasUserConsented(): boolean {
    return this.currentConsent.hasConsented;
  }

  // Update consent preferences
  updateConsent(updates: Partial<Omit<ConsentState, 'timestamp' | 'hasConsented'>>): void {
    this.currentConsent = {
      ...this.currentConsent,
      ...updates,
      hasConsented: true,
      timestamp: Date.now(),
    };
    
    this.saveConsent();
    this.notifyListeners();
  }

  // Accept all cookies
  acceptAll(): void {
    this.updateConsent({
      analytics: true,
      marketing: true,
      functional: true,
    });
  }

  // Reject all non-essential cookies
  rejectAll(): void {
    this.updateConsent({
      analytics: false,
      marketing: false,
      functional: true, // Always keep functional cookies
    });
  }

  // Subscribe to consent changes
  subscribe(listener: (consent: ConsentState) => void): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Notify all listeners of consent changes
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.getConsent());
      } catch (error) {
        console.warn('Error notifying consent listener:', error);
      }
    });
  }

  // Reset all consent (for testing or user request)
  resetConsent(): void {
    this.currentConsent = DEFAULT_CONSENT;
    this.saveConsent();
    this.notifyListeners();
  }

  // Check if specific consent type is granted
  isGranted(type: keyof Omit<ConsentState, 'hasConsented' | 'timestamp'>): boolean {
    return this.currentConsent[type];
  }
}

// Export singleton instance
export const consentManager = ConsentManager.getInstance();

// Utility hooks and functions
export const useConsent = () => {
  const [consent, setConsent] = useState(consentManager.getConsent());

  useEffect(() => {
    const unsubscribe = consentManager.subscribe(setConsent);
    return unsubscribe;
  }, []);

  return {
    consent,
    hasUserConsented: consentManager.hasUserConsented(),
    acceptAll: () => consentManager.acceptAll(),
    rejectAll: () => consentManager.rejectAll(),
    updateConsent: (updates: Partial<Omit<ConsentState, 'timestamp' | 'hasConsented'>>) => 
      consentManager.updateConsent(updates),
    resetConsent: () => consentManager.resetConsent(),
    isGranted: (type: keyof Omit<ConsentState, 'hasConsented' | 'timestamp'>) => 
      consentManager.isGranted(type),
  };
};